// Copyright (c) 2025 DTCC and/or its affiliates. All rights reserved.
// SPDX-License-Identifier: Apache-2.0
import { useNavigate } from 'react-router-dom';
import { Checkbox } from '@mui/material';
import { GridColDef, GridRowParams } from '@mui/x-data-grid';
import { AllowList } from '@daml.js/dtc-registry/lib/Dtc/Registry/V3/Rbac/AllowList';
import { ContractId } from '@daml/types';
import Loading from '../../../components/Loading';
import PartyDisplay from '../../../components/PartyDisplay';
import { DataTable } from '../../../components/Table/DataTable';
import { useLedgerExercise } from '../../../hooks/mutations/ledger/useLedgerExercise';
import useMutate from '../../../hooks/mutations/useMutate';
import useAllowListRole, {
  AllowListViewMode,
} from '../../../hooks/queries/registry/allowList/useAllowListRole';
import useAllowLists from '../../../hooks/queries/registry/allowList/useAllowLists';
import TemplateContract from '../../../utils/TemplateContract';
import { formatTimestamp, parseTimestampToDate } from '../../../utils/common';
import { encodeAllowListName } from '../../../utils/urls';
import { PageWrapper } from '../../PageWrapper';

type Row = TemplateContract<AllowList>;

type AllowListsProps = {
  mode?: AllowListViewMode;
};

const createHeaders: (
  mode: AllowListViewMode | null,
  onToggleMaster?: (contractId: ContractId<AllowList>, isOnMaster: boolean) => void,
) => GridColDef<Row>[] = (mode, onToggleMaster) => {
  return [
    {
      field: 'name',
      headerName: 'Name',
      valueGetter: (params) => params.row.payload.name,
      flex: 1,
      filterable: true,
    },
    {
      field: 'owner',
      headerName: 'Owner',
      valueGetter: (params) => params.row.payload.owner,
      renderCell: (params) => <PartyDisplay party={params.row.payload.owner} />,
      flex: 1,
      filterable: false,
    },
    {
      field: 'masterAllowListMembership',
      headerName: 'In Master',
      valueGetter: (params) => params.row.payload.masterAllowListMembership.status,
      renderCell: (params) => {
        const isOnMaster =
          params.row.payload.masterAllowListMembership.status === 'OnMasterAllowList';
        if (mode !== 'registrar') {
          return isOnMaster ? 'Yes' : 'No';
        }
        return (
          <div onClick={(e) => e.stopPropagation()}>
            <Checkbox
              checked={isOnMaster}
              onChange={() => onToggleMaster?.(params.row.contractId, isOnMaster)}
            />
          </div>
        );
      },
      flex: 1,
      filterable: false,
    },
    {
      field: 'lastUpdated',
      headerName: 'Updated',
      type: 'dateTime',
      valueGetter: (params) =>
        parseTimestampToDate(params.row.payload.masterAllowListMembership.lastUpdated),
      renderCell: (params) =>
        formatTimestamp(params.row.payload.masterAllowListMembership.lastUpdated),
      flex: 1,
      filterable: false,
    },
  ];
};

export const AllowLists = ({ mode: initialMode }: AllowListsProps) => {
  const navigate = useNavigate();
  const allowLists = useAllowLists();
  const { mode: detectedMode } = useAllowListRole();
  const mutate = useMutate();
  const addToMasterMutation = useLedgerExercise(AllowList.AllowList_AddToMasterAllowList);
  const removeFromMasterMutation = useLedgerExercise(AllowList.AllowList_RemoveFromMasterAllowList);

  const mode = initialMode ?? detectedMode;
  const pageName = mode === 'registrar' ? 'Master Allow List' : 'Allow Lists';

  const onRowClick = (r: GridRowParams<Row>) => {
    navigate(`/registry/allow-lists/${encodeAllowListName(r.row.payload.name)}`);
  };

  const onToggleMaster = async (contractId: ContractId<AllowList>, isOnMaster: boolean) => {
    if (isOnMaster) {
      await mutate(
        removeFromMasterMutation,
        { cid: contractId, argument: {} },
        'Removed from Master Allow List',
        'Failed to remove from Master Allow List',
      );
    } else {
      await mutate(
        addToMasterMutation,
        { cid: contractId, argument: {} },
        'Added to Master Allow List',
        'Failed to add to Master Allow List',
      );
    }
  };

  const isLoading = allowLists.isLoading || !allowLists.data;
  if (isLoading) return <Loading />;

  return (
    <PageWrapper pageName={pageName}>
      <DataTable
        columns={createHeaders(mode, (cid, isOnMaster) => {
          void onToggleMaster(cid, isOnMaster);
        })}
        rows={allowLists.data}
        defaultSortModel={[{ field: 'owner', sort: 'asc' }]}
        rowKey={(a) => a.contractId}
        onRowClick={onRowClick}
      />
    </PageWrapper>
  );
};
